import React from 'react';
import { Plane } from 'lucide-react';
import { getUserFacingBookingId } from '../../utils/userFacingBookingId';
import { fmtISTDate, fmtISTShort } from '../../utils/datetimeIST';
import { resolvePassengerDisplayName } from '../../utils/passengerDisplay';

export function BookingSummaryCard({ booking, snap, bookingId, isAdmin = false, className = '' }) {
  const b = booking || legacySnapToBooking(snap, bookingId);
  if (!b) return null;

  const segments = b.segments || [];
  const passengers = b.passengers || [];
  const isConfirmed = ['confirmed', 'CONFIRMED', 'ticketed', 'TICKETED'].includes(b.status);

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-3 space-y-4" data-testid="ticket-booking-summary">
      {/* Top row — booking id + (admin-only) operational badges */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 pb-3">
        <div>
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Booking Reference</div>
          <div className="font-mono text-sm font-semibold text-gray-900">{getUserFacingBookingId(b)}</div>
          {isAdmin && b.supplier_booking_id && b.supplier_booking_id !== b.booking_id && (
            <div className="text-[10px] text-gray-500 mt-0.5">Supplier Ref: <span className="font-mono">{b.supplier_booking_id}</span></div>
          )}
        </div>
        {isAdmin && (
          <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
            {b.source && <span className="bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded uppercase font-medium">{b.source}</span>}
            {b.booking_type && <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded uppercase">{b.booking_type}</span>}
            {b.payment_mode && <span className="bg-purple-100 text-purple-700 px-2 py-0.5 rounded">{b.payment_mode}</span>}
            {b.status && (
              <span className={`px-2 py-0.5 rounded uppercase font-medium ${isConfirmed ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>{b.status}</span>
            )}
            {b.ticket_status && b.ticket_status !== b.status && (
              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded uppercase font-medium">{b.ticket_status}</span>
            )}
          </div>
        )}
      </div>

      {/* PNR / Ticket numbers / Travel date — prominent row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        {b.pnr && <DetailPill label="Airline PNR" value={<span className="font-mono font-semibold text-blue-700">{b.pnr}</span>} />}
        {b.airline_pnr && b.airline_pnr !== b.pnr && (
          <DetailPill label="Airline PNR" value={<span className="font-mono font-semibold">{b.airline_pnr}</span>} />
        )}
        {b.riya_track_id && <DetailPill label="Riya Track ID" value={<span className="font-mono">{b.riya_track_id}</span>} />}
        {b.travel_date && <DetailPill label="Travel Date" value={fmtISTDate(b.travel_date)} />}
        {typeof b.total_amount === 'number' && (
          <DetailPill label={`Total (${b.currency || 'INR'})`} value={<span className="font-semibold">₹{b.total_amount.toLocaleString()}</span>} />
        )}
        {b.ticket_numbers?.length > 0 && (
          <DetailPill label="Ticket #" value={<span className="font-mono text-[11px]">{b.ticket_numbers.join(', ')}</span>} />
        )}
      </div>

      {/* Flight segments */}
      {segments.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Flight Details</div>
          {segments.map((s, i) => (
            <SegmentRow key={i} seg={s} />
          ))}
        </div>
      )}

      {/* Passengers — full list with individual PNR + ticket + pax info */}
      {passengers.length > 0 && (
        <div className="space-y-2">
          <div className="text-[10px] text-gray-500 uppercase tracking-wide">Passengers ({passengers.length})</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {passengers.map((p, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-2.5 text-xs" data-testid={`pax-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-gray-900 truncate">{resolvePassengerDisplayName(p)}</div>
                  <span className="text-[10px] bg-white px-1.5 py-0.5 rounded border border-gray-200 uppercase flex-shrink-0">{p.type}</span>
                </div>
                <div className="mt-1 space-y-0.5 text-gray-600">
                  {p.pnr && <div>PNR: <span className="font-mono font-medium text-gray-900">{p.pnr}</span></div>}
                  {p.ticket_number && <div>Ticket: <span className="font-mono text-gray-900">{p.ticket_number}</span></div>}
                  {p.nationality && <div>Nationality: {p.nationality}</div>}
                  {p.passport_number && <div>Passport: {p.passport_number}</div>}
                  {p.dob && <div>DOB: {fmtISTDate(p.dob)}</div>}
                  {p.seat && <div>Seat: {typeof p.seat === 'object' ? (p.seat.code || p.seat.seatNumber) : p.seat}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Contact & agent (agency info only visible in admin view) */}
      {(b.contact_email || b.contact_phone || (isAdmin && (b.agent_name || b.agency_name))) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs border-t border-gray-100 pt-3">
          {b.contact_email && <DetailPill label="Contact Email" value={<span className="truncate block">{b.contact_email}</span>} />}
          {b.contact_phone && <DetailPill label="Contact Phone" value={b.contact_phone} />}
          {isAdmin && b.agency_name && <DetailPill label="Agency" value={b.agency_name} />}
          {isAdmin && b.agent_name && <DetailPill label="Agent" value={b.agent_name} />}
        </div>
      )}
    </div>
  );
};

const SegmentRow = ({ seg }) => {
  const o = seg.origin || {};
  const d = seg.destination || {};
  const formatDt = (x) => fmtISTShort(x);
  const duration = (() => {
    if (!seg.departure_time || !seg.arrival_time) return seg.duration_minutes ? `${Math.floor(seg.duration_minutes / 60)}h ${seg.duration_minutes % 60}m` : null;
    const a = new Date(seg.departure_time).getTime();
    const b = new Date(seg.arrival_time).getTime();
    if (!a || !b || b <= a) return null;
    const mins = Math.round((b - a) / 60000);
    return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  })();

  return (
    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center flex-shrink-0">
            <Plane className="w-4 h-4 text-blue-600" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 text-sm truncate">{seg.airline || 'Flight'}</div>
            <div className="text-[11px] text-gray-500">
              {[seg.flight_number, seg.airline_code, seg.cabin_class].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-1 min-w-[240px] justify-center">
          <div className="text-right min-w-0">
            <div className="font-bold text-gray-900">{o.code || '—'}</div>
            <div className="text-[10px] text-gray-500 truncate">{o.city || o.name || ''}</div>
            <div className="text-[10px] text-gray-500">{formatDt(seg.departure_time)}</div>
            {o.terminal && <div className="text-[10px] text-gray-500">T{o.terminal}</div>}
          </div>
          <div className="flex flex-col items-center px-2 flex-shrink-0">
            {duration && <div className="text-[10px] text-gray-500">{duration}</div>}
            <div className="w-12 h-[1px] bg-gray-400 my-0.5"></div>
            <div className="text-[10px] text-gray-500">{seg.stops ? `${seg.stops} stop${seg.stops > 1 ? 's' : ''}` : 'Non-stop'}</div>
          </div>
          <div className="min-w-0">
            <div className="font-bold text-gray-900">{d.code || '—'}</div>
            <div className="text-[10px] text-gray-500 truncate">{d.city || d.name || ''}</div>
            <div className="text-[10px] text-gray-500">{formatDt(seg.arrival_time)}</div>
            {d.terminal && <div className="text-[10px] text-gray-500">T{d.terminal}</div>}
          </div>
        </div>
      </div>
      {(seg.baggage || seg.cabin_baggage) && (
        <div className="text-[10px] text-gray-500 mt-2 pt-2 border-t border-blue-100/60">
          {seg.baggage && <span className="mr-3">Check-in: {seg.baggage}</span>}
          {seg.cabin_baggage && <span>Cabin: {seg.cabin_baggage}</span>}
        </div>
      )}
    </div>
  );
};

const DetailPill = ({ label, value }) => (
  <div className="bg-gray-50 rounded-lg px-3 py-2 min-w-0">
    <div className="text-[10px] text-gray-500 uppercase tracking-wide">{label}</div>
    <div className="text-gray-900 font-medium text-sm mt-0.5 truncate">{value}</div>
  </div>
);

// Adapter — lets the admin queue (which only has `snap`) still render
// without bumping to the richer `booking` object.
const legacySnapToBooking = (snap, bookingId) => {
  if (!snap || !Object.keys(snap).length) return null;
  return {
    booking_id: snap.booking_id || bookingId,
    source: snap.source,
    status: snap.status,
    payment_mode: snap.payment_mode,
    pnr: snap.pnr,
    total_amount: snap.total_amount,
    segments: [{
      airline: snap.airline,
      airline_code: snap.airline_code,
      flight_number: snap.flight_number,
      origin: { code: snap.from_airport, city: null, terminal: null },
      destination: { code: snap.to_airport, city: null, terminal: null },
      departure_time: snap.depart_at,
      arrival_time: snap.arrive_at,
      stops: 0,
    }],
    passengers: (snap.passengers || []).map((p) => ({ full_name: p.name, type: p.type })),
  };
};


export default BookingSummaryCard;
